import React, { useState, useEffect, useRef } from 'react'
import { Button } from './ui'
import { 
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../constants'

const PWAInstallButton: React.FC = () => {
  const [isInstalled, setIsInstalled] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // Verificar se já está instalado
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true)
        return true
      }
      if ((window.navigator as any).standalone === true) {
        setIsInstalled(true)
        return true
      }
      return false
    }

    if (checkIfInstalled()) {
      return
    }

    // Verificar se o app já foi instalado
    const handleAppInstalled = () => {
      setIsInstalled(true)
    }

    window.addEventListener('appinstalled', handleAppInstalled)

    // Verificar periodicamente se foi instalado
    const interval = setInterval(() => {
      if (checkIfInstalled()) {
        clearInterval(interval)
      }
    }, 1000)

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled)
      clearInterval(interval)
    }
  }, [])

  // Fechar tooltip ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        buttonRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowTooltip(false)
      }
    }

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTooltip])

  // Não mostrar se já está instalado
  if (isInstalled) {
    return null
  }

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        onClick={() => setShowTooltip(!showTooltip)}
        onMouseEnter={() => setShowTooltip(true)}
        className="flex items-center gap-2 text-white"
        style={{ backgroundColor: BRAND_COLOR }}
        aria-label="Instalar App"
      >
        <DevicePhoneMobileIcon className="h-5 w-5" />
        Instalar App
      </Button>

      {showTooltip && (
        <div
          ref={tooltipRef}
          className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-6 z-50"
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="flex items-start gap-3 mb-4">
            <div 
              className="p-2 rounded-lg flex-shrink-0"
              style={{ backgroundColor: `${BRAND_COLOR}15` }}
            >
              <DevicePhoneMobileIcon 
                className="h-5 w-5"
                style={{ color: BRAND_COLOR }}
              />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Instalar PASI QG
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Instale o app no seu dispositivo para acesso rápido
              </p>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Acesso rápido direto da tela inicial</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Funciona offline com recursos em cache</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Experiência como app nativo</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Notificações e atualizações automáticas</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-3">
              Como instalar:
            </h4>
            <div className="space-y-3 text-sm text-blue-800 dark:text-blue-300">
              {(() => {
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
                const isAndroid = /Android/.test(navigator.userAgent)
                
                if (isIOS) {
                  return (
                    <ol className="list-decimal list-inside space-y-2">
                      <li>Toque no botão de compartilhar (ícone de compartilhar) na barra de navegação</li>
                      <li>Role para baixo e selecione "Adicionar à Tela de Início"</li>
                      <li>Toque em "Adicionar" para confirmar</li>
                      <li>O app aparecerá na sua tela inicial</li>
                    </ol>
                  )
                } else if (isAndroid) {
                  return (
                    <ol className="list-decimal list-inside space-y-2">
                      <li>Toque no menu do navegador (três pontos no canto superior direito)</li>
                      <li>Procure por "Adicionar à tela inicial" ou "Instalar app"</li>
                      <li>Toque na opção e confirme a instalação</li>
                      <li>O app será instalado e aparecerá na sua tela inicial</li>
                    </ol>
                  )
                } else {
                  return (
                    <ol className="list-decimal list-inside space-y-2">
                      <li>Procure pelo ícone de instalação na barra de endereços do navegador</li>
                      <li>Clique no ícone para realizar a instalação</li>
                      <li>O app será instalado e poderá ser aberto como um aplicativo separado na sua área de trabalho</li>
                    </ol>
                  )
                }
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PWAInstallButton
export { PWAInstallButton }
